import { useState, useCallback } from "react";
import { useI18n } from "./i18n";

export type DifficultyKey = "easy" | "normal" | "hard";

interface DifficultyOption {
  key: DifficultyKey;
  icon: string;
}

const DIFFICULTY_OPTIONS: DifficultyOption[] = [
  {
    key: "easy",
    icon: "\u{1F4A7}",
  },
  {
    key: "normal",
    icon: "\u{1F534}",
  },
  {
    key: "hard",
    icon: "\u{1F525}",
  },
];

const STORAGE_KEY = "bastion-difficulty";

/**
 * 从 localStorage 读取已持久化的难度设置。
 * 缺失或无效时回退到 'hard'。
 */
export function getStoredDifficulty(): DifficultyKey {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "easy" || stored === "normal" || stored === "hard") {
      return stored;
    }
  } catch {
    // 忽略存储错误——回退到默认值。
  }
  return "hard";
}

/**
 * Persist a difficulty choice to localStorage.
 */
export function saveDifficulty(key: DifficultyKey): void {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    // 存储不可用——静默忽略。
  }
}

interface DifficultySelectorProps {
  /** 玩家确认后以选中的难度键回调。 */
  onSelect: (key: DifficultyKey) => void;
  onClose: () => void;
}

/**
 * 在主菜单上显示的难度选择覆盖层。
 *
 * 渲染三张难度卡片及描述，将选择持久化到 localStorage，
 * 并通过 `onSelect` 通知调用方。
 */
export function DifficultySelector({ onSelect, onClose }: DifficultySelectorProps) {
  const [selected, setSelected] = useState<DifficultyKey>(getStoredDifficulty);
  const { t } = useI18n();

  const handleSelect = useCallback((key: DifficultyKey) => {
    setSelected(key);
  }, []);

  const handleConfirm = useCallback(() => {
    saveDifficulty(selected);
    onSelect(selected);
    onClose();
  }, [selected, onSelect, onClose]);

  return (
    <div className="difficulty-overlay" data-testid="difficulty-selector">
      <div className="difficulty-panel" data-testid="difficulty-panel">
        <div className="difficulty-header">
          <h2 className="difficulty-title">{t("difficulty_selector_title")}</h2>
          <button className="difficulty-close" data-testid="difficulty-close-btn" onClick={onClose}>
            X
          </button>
        </div>

        <div className="difficulty-options">
          {DIFFICULTY_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              className={`difficulty-card ${selected === opt.key ? "active" : ""}`}
              data-testid={`difficulty-${opt.key}-card`}
              onClick={() => handleSelect(opt.key)}
            >
              <span className="difficulty-card-icon" data-testid={`difficulty-${opt.key}-icon`}>
                {opt.icon}
              </span>
              <span className="difficulty-card-label" data-testid={`difficulty-${opt.key}-label`}>
                {t(`difficulty_${opt.key}` as keyof typeof t)}
              </span>
              <span className="difficulty-card-desc">
                {t(`difficulty_${opt.key}_desc` as keyof typeof t)}
              </span>
            </button>
          ))}
        </div>

        <div className="difficulty-actions">
          <button
            className="difficulty-btn primary"
            data-testid="difficulty-confirm-btn"
            onClick={handleConfirm}
          >
            {t("difficulty_confirm")}
          </button>
          <button
            className="difficulty-btn secondary"
            data-testid="difficulty-cancel-btn"
            onClick={onClose}
          >
            {t("difficulty_cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
